// src/components/ui/RouteChunkErrorBoundary.js
//
// Class-based error boundary (React error boundaries must be classes) that
// wraps the React.lazy/Suspense boundary in src/App.js — UI-SPEC State 3.
// Catches a failed lazy import() ("chunk-load failure") and renders a
// centered retry card. Any error that does NOT look like a chunk-load
// failure is re-thrown so Sentry.reactErrorHandler() (wired into index.js's
// createRoot onUncaughtError/onCaughtError/onRecoverableError options)
// still captures genuine app crashes — this boundary is scoped to
// chunk-load failures only, never a blanket catch-all (T-36-02-02).
//
// Renders ONLY the static State-3 copy below — never error.message or
// error.stack — no internal path/stack leakage to the user (T-36-02-01).
//
// No direct in-repo analog for the error-boundary mechanics (first class
// component / first componentDidCatch usage in this codebase — see
// 36-PATTERNS.md "No Analog Found"). Structural/visual shape (icon circle ->
// heading -> body -> single accent action) follows
// src/components/ui/EmptyStateCTA.js's card layout, danger-toned per
// UI-SPEC State 3.
//
// GAP-4 (Plan 36-08, corrected 36-07): "Try again" reloads the tab instead
// of retrying the failed import() in place. A browser permanently caches a
// REJECTED dynamic-import promise in its module map, so re-import()ing the
// same chunk URL — whether via a remounted Fragment (pre-36-07) or a freshly
// constructed React.lazy() instance keyed on a retry nonce (36-07) — returns
// the cached rejection rather than re-fetching, and could leave the Suspense
// fallback hanging (especially while still offline). A full reload clears
// the module cache, so the retried import() genuinely re-fetches — the
// reliable recovery this card's body copy already promises.

import React from 'react';
import Button from './primitives/Button';
import { checkVersionNow } from '../../hooks/useVersionWatch';
import { stashForReload, reloadForDeploy } from '../../utils/deployReload';

// Best-effort heuristic (RESEARCH.md A3 — no canonical cross-browser error
// shape for a failed dynamic import()). Covers the messages known bundlers
// and browsers throw for a chunk-load failure.
const CHUNK_LOAD_ERROR_PATTERN =
    /Failed to fetch dynamically imported module|Loading chunk .* failed|Importing a module script failed|error loading dynamically imported module/i;

function isChunkLoadError(error) {
    if (!error) return false;
    const message = typeof error.message === 'string' ? error.message : '';
    return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

// Inline heroicons-style alert-triangle SVG, strokeWidth 2 per this repo's
// icon convention — no external icon package is imported for this.
function AlertTriangleIcon() {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            style={{ color: 'var(--color-danger)' }}
        >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
        </svg>
    );
}

class RouteChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(caughtError) {
        if (!isChunkLoadError(caughtError)) {
            // Not a chunk-load failure — re-throw so the existing Sentry
            // error handlers (index.js createRoot options) still see it.
            // This boundary is scoped to chunk-load failures only.
            throw caughtError;
        }
        // A chunk that will not load usually means a deploy replaced it: ask
        // the version watcher right now. Never let this throw from the boundary.
        try {
            Promise.resolve(checkVersionNow({ force: true })).catch(() => {});
        } catch {
            // best effort
        }
    }

    handleRetry() {
        // GAP-4 (Plan 36-08): reload rather than reset-in-place. See the
        // header comment for why an in-place re-import cannot reliably
        // recover. Guarded for non-browser/test environments.
        if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
            stashForReload();
            reloadForDeploy({ reason: 'chunk-error', automatic: false });
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex-1 flex items-center justify-center p-10">
                    <div className="text-center" style={{ maxWidth: 400 }}>
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: 'var(--surface-danger)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                            }}
                        >
                            <AlertTriangleIcon />
                        </div>

                        <p
                            className="font-semibold"
                            style={{ color: 'var(--text-primary)', marginTop: 16, fontSize: 20, lineHeight: 1.3 }}
                        >
                            This page couldn&apos;t load
                        </p>
                        <p
                            className="text-sm"
                            style={{ color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}
                        >
                            Check your connection and try again. If it keeps happening, refresh the tab.
                        </p>

                        <div style={{ marginTop: 24 }}>
                            <Button variant="accent" size="md" onClick={this.handleRetry}>
                                Try again
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default RouteChunkErrorBoundary;
